import React, {useState, useEffect, createContext, useContext} from "react"
import {contextUpdates} from "@frontapp/plugin-sdk"
import {useOktaAuth} from "@okta/okta-react"

const isEmbedded = () => {
	try {
		return window.self !== window.top
	} catch {
		return true
	}
}

function useAgent(): [string, string] | [] {
	const context = useContext(AgentContext)
	if (!context) {
		throw new Error("useAgent must be used within AgentProvider")
	}
	return context.agent
}

type AgentContextType = {
	agent: [string, string] | []
}

const AgentContext = createContext<AgentContextType | null>(null)

const AgentProvider = ({children}: {children: React.ReactNode}) => {
	const [agent, setAgent] = useState<[string, string] | []>([])
	const [fallbackAgent, setFallbackAgent] = useState<[string, string] | []>([])
	const oktaObject = useOktaAuth()
	const oktaAuth = oktaObject?.oktaAuth

	useEffect(() => {
		const urlSearchParams = new URLSearchParams(window.location.search)
		const agentEmail = urlSearchParams.get("agent_email")
		const agentName = urlSearchParams.get("agent_name")

		if (agentEmail && agentName) {
			setFallbackAgent([agentEmail, agentName])
		}
	}, [])

	useEffect(() => {
		if (isEmbedded()) {
			const subscription = contextUpdates.subscribe(({teammate}) => {
				if (teammate.email) {
					setAgent([teammate.email, teammate.name])
				}
			})
			return () => {
				subscription.unsubscribe()
			}
		} else {
			oktaAuth?.getUser().then((user) => {
				if (user.email && user.name) {
					setAgent([user.email, user.name])
				}
			})
		}
	}, [oktaAuth])

	const agentValue = agent.length > 0 ? agent : fallbackAgent

	return <AgentContext.Provider value={{agent: agentValue}}>{children}</AgentContext.Provider>
}

export {useAgent, AgentProvider}
